import { Flex, Form, InputNumber, Select } from "antd";
import { CSSProperties, useEffect, useState } from "react";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { ClassCategory } from "shared/types/enum";
import { textPx } from "shared/constants/zoom";

/**
 * Shared class-form pieces, used by both the create modal and the edit form so
 * the capacity/duration semantics live in exactly one place:
 *
 * - CapacityField — adapts to the class type. Open: a maximum cap (number
 *   input, the session may run with fewer). Private: an exact party size
 *   (preset select Private/Duet/Triplet + an "Other" exact count above 3).
 *   Both write the same `max_participants` form value.
 * - DurationField — Jam + Menit inputs over a single total-minutes `duration`
 *   value, so the data model and scheduling math are unchanged.
 * - formatClassDuration — the one display formatter ("1 Jam 15 Menit").
 */

// ── Duration ──────────────────────────────────────────────────────────────

// "1 Jam", "1 Jam 15 Menit", "45 Menit"
export const formatClassDuration = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const hourUnit = translate("class_form.hour_unit");
  const minuteUnit = translate("class_form.minute_unit");
  if (hours && minutes) return `${hours} ${hourUnit} ${minutes} ${minuteUnit}`;
  if (hours) return `${hours} ${hourUnit}`;
  return `${minutes} ${minuteUnit}`;
};

const unitCaption: CSSProperties = {
  fontSize: textPx(11),
  color: colors.ink3,
  textAlign: "center",
  marginTop: 5,
};

// Form control over the single total-minutes value (value/onChange injected by
// Form.Item) — hours free, minutes 0–59.
const DurationInput = ({
  value,
  onChange,
}: {
  value?: number;
  onChange?: (totalMinutes: number) => void;
}) => {
  const hours = Math.floor((value ?? 0) / 60);
  const minutes = (value ?? 0) % 60;
  return (
    <Flex gap={10} style={{ maxWidth: 260 }}>
      <div style={{ flex: 1 }}>
        <InputNumber
          min={0}
          value={hours}
          onChange={(h) => onChange?.((h ?? 0) * 60 + minutes)}
          style={{ width: "100%" }}
        />
        <div style={unitCaption}>{translate("class_form.hours")}</div>
      </div>
      <div style={{ flex: 1 }}>
        <InputNumber
          min={0}
          max={59}
          value={minutes}
          onChange={(m) => onChange?.(hours * 60 + (m ?? 0))}
          style={{ width: "100%" }}
        />
        <div style={unitCaption}>{translate("class_form.minutes")}</div>
      </div>
    </Flex>
  );
};

// Kredit per Booking: what one booking of this class takes, the same on every
// package that covers it (Andrew's team, 2026-10-20: one number per class). A
// plain typed field, no spinner handles. The server refuses a raise that a
// live tier covering the class could not pay for.
export const CreditCostField = () => (
  <Form.Item
    name="credit_cost"
    label={translate("class_form.credit_cost")}
    extra={translate("class_form.credit_cost_hint")}
    rules={[
      {
        required: true,
        type: "number",
        min: 1,
        max: 99,
        message: translate("form.error.credit_cost"),
      },
    ]}
  >
    <InputNumber
      min={1}
      max={99}
      precision={0}
      controls={false}
      style={{ width: "100%", maxWidth: 160 }}
      suffix={translate("product_catalog.class_credits_unit")}
    />
  </Form.Item>
);

export const DurationField = () => (
  <Form.Item
    name="duration"
    label={translate("class_form.duration")}
    rules={[
      {
        required: true,
        type: "number",
        min: 1,
        message: `${translate("form.error.input")} ${translate(
          "class_form.duration"
        )}`,
      },
    ]}
  >
    <DurationInput />
  </Form.Item>
);

// ── Capacity ──────────────────────────────────────────────────────────────

const OTHER = "other";

// Form control for the private exact party size: preset select (1 = Private,
// 2 = Duet, 3 = Triplet) plus an "Other" mode revealing a number input —
// Other is only for counts of 4+. Still the plain max_participants number
// underneath.
const PartySizeInput = ({
  value,
  onChange,
}: {
  value?: number;
  onChange?: (size: number | undefined) => void;
}) => {
  // The mode only auto-upgrades (value > 3 → Other, e.g. the edit form
  // hydrating) — never downgrades mid-typing, so entering "1" on the way to
  // "12" doesn't kick the admin out of the Other input. A custom entry that
  // matches a preset snaps back to it on blur instead.
  const [otherMode, setOtherMode] = useState(() => value != null && value > 3);
  useEffect(() => {
    if (value != null && value > 3) setOtherMode(true);
  }, [value]);

  return (
    <Flex vertical gap={8} style={{ maxWidth: 260 }}>
      <Select<number | typeof OTHER>
        value={otherMode ? OTHER : value}
        placeholder={`${translate("form.placeholders.select")} ${translate(
          "class_form.party_size"
        )}`}
        onChange={(v) => {
          if (v === OTHER) {
            setOtherMode(true);
            onChange?.(undefined);
          } else {
            setOtherMode(false);
            onChange?.(v);
          }
        }}
        options={[
          { value: 1, label: translate("class_form.preset_private") },
          { value: 2, label: translate("class_form.preset_duet") },
          { value: 3, label: translate("class_form.preset_triplet") },
          { value: OTHER, label: translate("class_form.preset_other") },
        ]}
      />
      {otherMode && (
        <InputNumber
          min={1}
          value={value}
          onChange={(v) => onChange?.(v ?? undefined)}
          onBlur={() => {
            // 1/2/3 are the presets — snap the selection back to them; only
            // 4+ stays as a custom Other entry.
            if (value != null && value >= 1 && value <= 3) setOtherMode(false);
          }}
          placeholder={translate("class_form.other_count")}
          style={{ width: "100%" }}
        />
      )}
    </Flex>
  );
};

// One field, two presentations over the same max_participants value: a max cap
// for Open, an exact party size for Private. Disabled until a type is chosen
// (create modal); the edit form passes the class's fixed type.
export const CapacityField = ({
  classType,
}: {
  classType?: ClassCategory;
}) => {
  const isPrivate = classType === ClassCategory.Private;
  return (
    <Form.Item
      name="max_participants"
      label={translate(
        isPrivate ? "class_form.party_size" : "class_form.max_capacity"
      )}
      extra={
        classType
          ? translate(isPrivate ? "class_form.hint_private" : "class_form.hint_open")
          : undefined
      }
      rules={[
        {
          required: true,
          type: "number",
          min: 1,
          message: translate("form.error.max_participants"),
        },
      ]}
    >
      {isPrivate ? (
        <PartySizeInput />
      ) : (
        <InputNumber
          min={1}
          disabled={!classType}
          style={{ width: "100%", maxWidth: 160 }}
        />
      )}
    </Form.Item>
  );
};
